import React, { useState } from 'react'

import classNames from 'classnames'

export type SelectorItem<V = string> = { value: V; text: string; disabled?: boolean }

interface Props<V = string> {
  items: SelectorItem<V>[]
  onSelect: (value: V) => void
  className?: string
  disabledIndicator?: JSX.Element
}

/**
 * This component contains a list of items that are shown as a scrollable list where a single item
 * can be selected from it. Its similar to dropdown that is just always open.
 */
export default function <V>({
  items,
  onSelect,
  className,
  disabledIndicator,
}: Props<V>): React.ReactElement {
  const [selectedItem, setSelectedItem] = useState<V | null>(null)
  return (
    <div
      className={classNames(
        'overflow-y-auto border border-gray-border dark:border-gray-border-dm p-1 max-h-[97px] rounded flex flex-col',
        className
      )}>
      {items.map((item) => (
        <button
          key={`scroll-item-${item.value}`}
          data-testid={`scroll-item-${item.value}`}
          disabled={item.disabled}
          type="button"
          className={classNames(
            'text-left p-2 rounded disabled:text-gray-dim disabled:cursor-not-allowed flex flex-row justify-between items-center',
            {
              'bg-gray-highlight dark:bg-gray-highlight-dm': item.value === selectedItem,
              'hover:bg-gray-light dark:hover:bg-gray-tabs-dm': item.value !== selectedItem,
            }
          )}
          onClick={() => {
            onSelect(item.value)
            setSelectedItem(item.value)
          }}>
          <span>{item.text}</span>
          {item.disabled && disabledIndicator}
        </button>
      ))}
    </div>
  )
}
